<template>
  <div class="page-admin-opportunity-settings">
    <BaseLoadingBar is-small v-if="pending" />
    <div class="page-admin-opportunity-settings__card">
      <h2 class="page-admin-opportunity-settings__title">
        {{ $t('page.admin-opportunity-settings.title') }}
      </h2>
      <hr class="page-admin-opportunity-settings__separator" />

      <div :class="{ 'page-admin-opportunity-settings__fields--readonly': !canEdit }">
        <div class="page-admin-opportunity-settings__field">
          <div class="page-admin-opportunity-settings__field-row">
            <span class="page-admin-opportunity-settings__field-label">
              {{ $t('page.admin-opportunity-settings.auto-create-label') }}
            </span>
            <BaseToggle :value="autoCreateFromTenders" @change="onToggleAutoCreate" :disabled="!canEdit" />
          </div>
          <div class="page-admin-opportunity-settings__field-subtitle">
            <span>{{ $t('page.admin-opportunity-settings.runs-hourly-note') }}</span>
            <BaseIcon name="clock" class="page-admin-opportunity-settings__note-icon" />
          </div>
        </div>

        <div
          class="page-admin-opportunity-settings__field"
          :class="{ 'page-admin-opportunity-settings__field--disabled': !autoCreateFromTenders }"
        >
          <div class="page-admin-opportunity-settings__field-row">
            <span class="page-admin-opportunity-settings__field-label">
              {{ $t('page.admin-opportunity-settings.lookback-hours-label') }}
              <BaseIcon
                name="info"
                class="page-admin-opportunity-settings__field-info-icon"
                v-tooltip="$t('page.admin-opportunity-settings.lookback-hours-hint')"
              />
            </span>
            <FormInput
              v-model.number="lookbackHours"
              type="number"
              name="lookbackHours"
              :error="lookbackHoursError"
              :disabled="!autoCreateFromTenders || !canEdit"
              :min="LOOKBACK_MIN"
              :max="LOOKBACK_MAX"
              step="1"
              small-input
              is-compact
            />
          </div>
        </div>
      </div>

      <div class="page-admin-opportunity-settings__actions" v-if="canEdit">
        <BaseButton @click.native="save" :disabled="saving || hasNotChanged || !isFormValid" :isLoading="saving">
          {{ $t('actions.save') }}
        </BaseButton>
      </div>
      <div class="page-admin-opportunity-settings__readonly-message" v-else>
        <span>{{ $t('page.admin-opportunity-settings.readonly-message') }}</span>
        <BaseIcon name="lock" class="page-admin-opportunity-settings__readonly-icon" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseButton from '@/components/base/BaseButton';
import BaseToggle from '@/components/base/BaseToggle';
import BaseIcon from '@/components/base/BaseIcon';
import FormInput from '@/components/form/FormInput';

const LOOKBACK_MIN = 1;
const LOOKBACK_MAX = 720; // 30 days in hours

export default {
  name: 'PageAdminOpportunitySettings',
  components: {
    BaseLoadingBar,
    BaseButton,
    BaseToggle,
    BaseIcon,
    FormInput,
  },
  data() {
    return {
      autoCreateFromTenders: false,
      lookbackHours: 48,
      LOOKBACK_MIN,
      LOOKBACK_MAX,
    };
  },
  computed: {
    ...mapGetters('tenantOpportunitySettings', ['settings', 'pending', 'saving']),
    ...mapGetters('auth', ['isAdminTenant']),
    canEdit() {
      return this.isAdminTenant;
    },
    lookbackHoursError() {
      if (!this.autoCreateFromTenders) return null;
      const v = this.lookbackHours;
      if (v === '' || v === null || v === undefined || Number.isNaN(v)) {
        return this.$t('page.admin-opportunity-settings.validation.required');
      }
      if (!Number.isInteger(v)) {
        return this.$t('page.admin-opportunity-settings.validation.integer');
      }
      if (v < LOOKBACK_MIN) {
        return this.$t('page.admin-opportunity-settings.validation.min', { min: LOOKBACK_MIN });
      }
      if (v > LOOKBACK_MAX) {
        return this.$t('page.admin-opportunity-settings.validation.max', { max: LOOKBACK_MAX });
      }
      return null;
    },
    isFormValid() {
      return !this.lookbackHoursError;
    },
    hasNotChanged() {
      return (
        this.autoCreateFromTenders === this.settings.autoCreateFromTenders &&
        this.lookbackHours === this.settings.lookbackHours
      );
    },
  },
  methods: {
    ...mapActions('tenantOpportunitySettings', ['getSettings', 'updateSettings']),
    onToggleAutoCreate(value) {
      this.autoCreateFromTenders = value;
    },
    syncForm(settings) {
      this.autoCreateFromTenders = settings.autoCreateFromTenders;
      this.lookbackHours = settings.lookbackHours;
    },
    async save() {
      if (!this.isFormValid) return;
      try {
        await this.updateSettings({
          autoCreateFromTenders: this.autoCreateFromTenders,
          lookbackHours: this.lookbackHours,
        });
        this.$toast.success(this.$t('page.admin-opportunity-settings.save-success'));
      } catch (e) {
        this.$toast.error(e);
      }
    },
  },
  async created() {
    try {
      await this.getSettings();
      this.syncForm(this.settings);
    } catch (e) {
      this.$toast.error(e);
    }
  },
};
</script>

<style lang="scss">
.page-admin-opportunity-settings {
  padding: 32px;

  &__card {
    background: $color-white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
    max-width: 600px;
    padding: 32px;
  }

  &__title {
    color: $color-normal;
    font-size: $font-size-xxbigger;
    margin: 0 0 0 0;
  }

  &__separator {
    border: none;
    border-top: 1px solid $color-border;
    margin: 24px 0;
  }

  &__field-subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $color-neutral;
    margin-top: 6px;
  }

  &__note-icon {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }

  &__fields--readonly {
    .page-admin-opportunity-settings__field-label {
      color: $color-neutral;
    }
  }

  &__field {
    margin-bottom: 24px;

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__field-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__field-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: $color-normal;
    flex: 1;
    min-width: 0;
  }

  &__field-info-icon {
    width: 16px;
    height: 16px;
    color: $color-neutral;
    cursor: help;
  }

  // FormInput has width:100% by default. Pin it to the small-input size so
  // it doesn't compete with the flex:1 label for space.
  // 200px gives enough room for error text without wrapping to many lines.
  .form-input.small-input {
    width: 200px;
    flex-shrink: 0;

    // Right-align the input within the wider container.
    // display:block is required — margin-left:auto has no effect on inline
    // replaced elements (which <input> is by default).
    .form-input__input {
      display: block;
      margin-left: auto;
    }

    // Render the error inline (static flow) so it pushes the container
    // downward instead of growing upward and overlapping fields above.
    // Right-align so it sits flush under the input.
    .form-input__error-msg {
      position: static;
      display: block;
      margin-top: 4px;
      text-align: right;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
  }

  &__readonly-message {
    font-size: 13px;
    color: $color-neutral;
    margin-top: 8px;
  }

  &__readonly-icon {
    width: 13px;
    height: 13px;
    vertical-align: middle;
    margin-left: 4px;
  }
}
</style>
